import {
    type AudioSourceData,
    type MediaSourceData,
    type Scene,
    type SceneGroup,
    type TimeAwareMediaAsset,
    type TranscriptData,
    VideoData,
} from '@lumen5/luminary';

import type { SceneDesign, TemplateStatusEnum } from 'js/src/client';
import { SourceEnum } from 'js/src/client';
import { RecommendationDefaults } from 'js/src/client/types.gen';
import type { SnapPoint } from 'js/src/components/common/hooks/useDraggable';
import type { DisplayGroupId, SceneDesignId, StoredDisplayGroup } from 'js/src/data/templates';
import {
    ANIMATED_ZOOM_OPTION_KEY,
    DYNAMIC_DISPLAY_GROUPS,
    SceneDesignSpecialCase,
    WATERMARK_ASSET_NAME,
    withStableEmptyArray,
} from 'js/src/libs/consts';
import {
    isAIAvatarSceneGroup,
    isTalkingHeadSceneGroup,
    isVoiceoverSceneGroup,
} from 'js/src/luminary';
import { getNormalizedAssetBounds } from 'js/src/luminary/luminary-utils';

interface scenePresentationMap {
    sceneToDesignMap: Record<string, SceneDesignId>;
    scenesToShow: Record<DisplayGroupId, SceneDesignId[]>;
    designToSceneMap: Record<SceneDesignId, string>;
    sceneDesignStatusMap: Record<SceneDesignId, TemplateStatusEnum>;
    sceneDesignDisplayNameMap: Record<SceneDesignId, string | null>;
}

export interface sceneAndSceneDesign {
    scene: Scene;
    sceneDesign: SceneDesign;
    specialCase?: SceneDesignSpecialCase;
}

type options = {
    includeOutroDesigns?: boolean;
    includeCaptionDesigns?: boolean;
    includeTalkingHeadOverlayDesigns?: boolean;
};

/**
 * Build the maps needed to present scene designs in scene selector and template editor.
 */
export function buildScenePresentationMaps(
    scenesAndSceneDesigns: readonly sceneAndSceneDesign[],
    opts?: options,
): scenePresentationMap {
    const sceneToDesignMap: Record<string, SceneDesignId> = {};
    const scenesToShow: Record<DisplayGroupId, SceneDesignId[]> = {};
    const sceneDesignDisplayNameMap: Record<string, string | null> = {};
    const sceneDesignStatusMap: Record<SceneDesignId, TemplateStatusEnum> = {};
    const designToSceneMap = scenesAndSceneDesigns.reduce(
        (
            mapping: Record<SceneDesignId, string>,
            { scene, sceneDesign, specialCase = SceneDesignSpecialCase.NONE },
        ) => {
            let displayGroup = sceneDesign.display_group;
            const sceneDesignId = sceneDesign.id;
            const isMultipleSpeakers = specialCase === SceneDesignSpecialCase.MultipleSpeakers;
            if (isMultipleSpeakers) {
                displayGroup = DYNAMIC_DISPLAY_GROUPS[specialCase];
            }

            const isOutro = scene.sceneCategory === VideoData.CATEGORY_OUTRO;
            const isCaption = scene.sceneCategory === VideoData.CATEGORY_CAPTION;
            const shouldHide =
                (isOutro && !opts?.includeOutroDesigns) ||
                (isCaption && !opts?.includeCaptionDesigns);

            if (!shouldHide) {
                if (!scenesToShow[displayGroup]) {
                    scenesToShow[displayGroup] = [];
                }
                scenesToShow[displayGroup].push(sceneDesignId);
                sceneDesignStatusMap[sceneDesignId] = sceneDesign.status;
                sceneDesignDisplayNameMap[sceneDesignId] = sceneDesign.display_name;
            }
            if (!isMultipleSpeakers) {
                mapping[sceneDesignId] = scene.id;
                sceneToDesignMap[scene.id] = sceneDesignId;
            }
            return mapping;
        },
        {},
    );

    return {
        sceneToDesignMap,
        scenesToShow, // Display group id => scene design id[]
        designToSceneMap, // Scene design id => scene.id
        sceneDesignStatusMap,
        sceneDesignDisplayNameMap, // scene design id => display name
    };
}

/**
 * Slots the animated-zoom option in beside the scene design it duplicates, so it reads as a variant
 * of what is already on screen rather than a section of its own. Not offered for outros: an outro
 * is a static end card, not source footage to zoom into.
 */
export function insertAnimatedZoomOption(
    scenesToShow: Record<DisplayGroupId, SceneDesignId[]>,
    sourceSceneDesignId: string | null,
    sceneGroupCategory: string | null,
): void {
    if (sceneGroupCategory === VideoData.CATEGORY_OUTRO) {
        return;
    }
    const displayGroups = Object.keys(scenesToShow);
    if (displayGroups.length === 0) {
        return;
    }
    const groupWithSource = displayGroups.find(
        (displayGroup) =>
            !!sourceSceneDesignId &&
            scenesToShow[Number(displayGroup)].includes(sourceSceneDesignId),
    );
    const options = scenesToShow[Number(groupWithSource ?? displayGroups[0])];
    const sourceIndex = sourceSceneDesignId ? options.indexOf(sourceSceneDesignId) : -1;
    options.splice(sourceIndex + 1, 0, ANIMATED_ZOOM_OPTION_KEY);
}

/**
 * Returns only display groups for which we have scene designs to show.
 */
export function getDisplayGroupsToShow(
    templateDisplayGroups: readonly StoredDisplayGroup[],
    sceneDesignsToShow: Record<number, string[]>,
): readonly StoredDisplayGroup[] {
    return withStableEmptyArray(
        templateDisplayGroups.filter((displayGroup) => {
            return Object.keys(sceneDesignsToShow).includes(displayGroup.id.toString());
        }),
    );
}

/**
 * Returns a list of linked media assets for a scene. MediaAssets on the scene level (or below) can have
 * linkToSceneGroup = true, which means that they kind of act as "windows" through which the sceneGroup.mediaAsset
 * can be seen. Note that not all designs will have this asset, so the function could return an empty list.
 */
export function getLinkedMediaAssets(scene: Scene): TimeAwareMediaAsset[] {
    return scene
        .getAssetsByType(VideoData.MEDIA_ASSET_TYPE)
        .filter((media) => media.linkToSceneGroup);
}

/**
 * Delete all captions in a sceneGroup and replace with placeholder captions. This will also
 * reset the caption style to the provided captionSceneDesignId.
 */
export function resetCaptions(sceneGroup: SceneGroup, captionSceneDesignId: string) {
    if (!sceneGroup) {
        return;
    }
    let placeholderText = null;
    if (isTalkingHeadSceneGroup(sceneGroup) || isAIAvatarSceneGroup(sceneGroup)) {
        placeholderText = TH_TRANSCRIPT as TranscriptData;
    } else if (isVoiceoverSceneGroup(sceneGroup)) {
        placeholderText = VO_TRANSCRIPT as TranscriptData;
    }
    if (placeholderText) {
        sceneGroup.captions.sceneDesignId = captionSceneDesignId;
        const captions = sceneGroup.captions;
        captions.transcript.transcriptData = placeholderText;
    }
}

export const TALKING_HEAD_PLACEHOLDER_MEDIA_ASSET_SOURCEDATA: MediaSourceData = {
    uuid: '00000000-0000-0000-0000-000000000000',
    title: null,
    type: 'video',
    source: 28,
    source_id: 'https://storage.googleapis.com/lumen5-prod-images/talking-head-placeholder.mp4',
    license: VideoData.USER_PRIVATE_LICENSE,
    is_editorial: false,
    width: 2880,
    height: 1920,
    thumbnail_url: 'https://storage.googleapis.com/lumen5-prod-images/talking-head-thumbnail.mp4',
    preview_url:
        'https://storage.googleapis.com/lumen5-prod-images/talking-head-placeholder-preview.mp4',
    render_url: 'https://storage.googleapis.com/lumen5-prod-images/talking-head-placeholder.mp4',
    poster_url:
        'https://storage.googleapis.com/lumen5-prod-images/talking-head-placeholder-poster.png',
    duration: 3400,
    has_audio: false,
    has_voiceover: null,
};

export const AVATAR_BACKGROUND_PLACEHOLDER_MEDIA_ASSET_URL =
    'https://storage.googleapis.com/lumen5-prod-images/avatar_background_small.jpg';

export const AVATAR_BACKGROUND_PLACEHOLDER_MEDIA_ASSET_SOURCEDATA: MediaSourceData = {
    uuid: '31263120-5ee9-4bc9-9a3b-90b6069a27bb',
    source: SourceEnum.SOURCE_AI_AVATAR,
    source_id: '31263120-5ee9-4bc9-9a3b-90b6069a27bc',
    license: VideoData.USER_PRIVATE_LICENSE,
    width: 1920,
    height: 1149,
    thumbnail_url:
        'https://storage.googleapis.com/lumen5-prod-images/avatar_background_thumbnail_text_40.jpg',
    preview_url:
        'https://storage.googleapis.com/lumen5-prod-images/avatar_background_preview_text_40.jpg',
    render_url: 'https://storage.googleapis.com/lumen5-prod-images/avatar_background_large.jpg',
    is_editorial: false,
    type: 'image',
    title: 'avatar background image',
};

export const VOICEOVER_PLACEHOLDER_ASSET_SOURCE: AudioSourceData = {
    id: 551905,
    uuid: '6b2dfd6c-c669-40c8-a870-efdb4001cdf6',
    source: 28,
    source_id: 'lumen5-user-28da9b69-a362-4c97-866f-38a0091cbc09/silentmp3zsg9jZ.normalized.mp3',
    license: VideoData.USER_PRIVATE_LICENSE,
    attribution: '',
    mp3_url:
        'https://storage.googleapis.com/lumen5-user-28da9b69-a362-4c97-866f-38a0091cbc09/silentmp3zsg9jZ.normalized.mp3',
    duration: 3,
    description: '',
    tags: [],
    title: 'silent',
    name: 'silent',
    quality: 0,
    ai_voice: null,
    ai_voice_speed: null,
};

const TH_TRANSCRIPT = {
    words: [
        {
            word: 'Drag',
            startTime: 0,
            endTime: 600,
        },
        {
            word: 'an',
            startTime: 600,
            endTime: 800,
        },
        {
            word: 'uploaded',
            startTime: 800,
            endTime: 1300,
        },
        {
            word: 'clip',
            startTime: 1300,
            endTime: 1700,
        },
        {
            word: 'with',
            startTime: 2000,
            endTime: 2200,
        },
        {
            word: 'a',
            startTime: 2200,
            endTime: 2300,
        },
        {
            word: 'speaker',
            startTime: 2300,
            endTime: 3000,
        },
        {
            word: 'to',
            startTime: 3000,
            endTime: 3300,
        },
        {
            word: 'add',
            startTime: 3300,
            endTime: 3800,
        },
        {
            word: 'captions',
            startTime: 3800,
            endTime: 4300,
        },
        {
            word: 'here.',
            startTime: 4300,
            endTime: 4700,
        },
    ],
    language: 'en',
    textDirection: 'ltr',
    separateWordsWithSpaces: true,
};

const VO_TRANSCRIPT = {
    words: [
        {
            endTime: 3400,
            startTime: 0,
            word: 'Please',
        },
        {
            endTime: 4100,
            startTime: 3400,
            word: 'drag',
        },
        {
            endTime: 4200,
            startTime: 4100,
            word: 'a',
        },
        {
            endTime: 5000,
            startTime: 4200,
            word: 'voiceover',
        },
        {
            endTime: 5400,
            startTime: 5000,
            word: 'audio',
        },
        {
            endTime: 5700,
            startTime: 5400,
            word: 'file',
        },
        {
            endTime: 6000,
            startTime: 5700,
            word: 'here',
        },
        {
            endTime: 6300,
            startTime: 6000,
            word: 'to',
        },
        {
            endTime: 6600,
            startTime: 6300,
            word: 'add',
        },
        {
            endTime: 7200,
            startTime: 6600,
            word: 'captions,',
        },
        {
            endTime: 7600,
            startTime: 7200,
            word: 'or',
        },
        {
            endTime: 8400,
            startTime: 8100,
            word: 'use',
        },
        {
            endTime: 8600,
            startTime: 8400,
            word: 'the',
        },
        {
            endTime: 9200,
            startTime: 8600,
            word: 'transcript',
        },
        {
            endTime: 9700,
            startTime: 9200,
            word: 'editor',
        },
        {
            endTime: 10000,
            startTime: 9800,
            word: 'to',
        },
        {
            endTime: 10300,
            startTime: 10000,
            word: 'add',
        },
        {
            endTime: 10800,
            startTime: 10300,
            word: 'visual',
        },
        {
            endTime: 11800,
            startTime: 10900,
            word: 'overlays.',
        },
    ],
    language: 'en',
    textDirection: 'ltr',
    separateWordsWithSpaces: true,
};

/*
 * Given a list of highlight indices and a list of chunks, return a list of lists of highlight indices
 * This is used so that highlight indices relative to a full segment can be used to highlight the text
 * on each subscene after the original segment has been split into subscenes
 */
export function splitHighlightsAcrossSubscenes(
    highlights: readonly (readonly number[])[],
    chunks: string[],
    subsceneSeparator: string = RecommendationDefaults.SUBSCENE_TOKEN,
): [number, number][][] {
    let chunkStart = 0;
    const highlightChunks: [number, number][][] = [];
    const tmpChunks = chunks.slice();
    for (let i = 0; i < tmpChunks.length; i++) {
        if (i < tmpChunks.length - 1) {
            tmpChunks[i] += subsceneSeparator;
        }
    }
    for (const chunk of tmpChunks) {
        const chunkEnd = chunkStart + chunk.length;
        const chunkHighlights: [number, number][] = [];
        for (const [start, end] of highlights) {
            if (start < chunkEnd && end > chunkStart) {
                const adjustedStart = Math.max(start, chunkStart) - chunkStart;
                let adjustedEnd = Math.min(end, chunkEnd) - chunkStart;
                if (adjustedEnd > chunk.replace(subsceneSeparator, '').length) {
                    adjustedEnd = chunk.replace(subsceneSeparator, '').length;
                }
                chunkHighlights.push([adjustedStart, adjustedEnd]);
            }
        }
        highlightChunks.push(chunkHighlights);
        chunkStart = chunkEnd;
    }
    return highlightChunks;
}

export interface SubsceneFormatting {
    highlights: [number, number][];
    bold: [number, number][];
    italic: [number, number][];
    underline: [number, number][];
}

/*
 * Remap all four inline-formatting range arrays (highlight + bold/italic/underline) from the full
 * pre-split text onto each || chunk, so a set_text that both splits into subscenes and carries B/I/U
 * keeps each chunk's formatting. Delegates to splitHighlightsAcrossSubscenes per kind and returns
 * one SubsceneFormatting per chunk.
 */
export function splitFormattingAcrossSubscenes(
    formatting: SubsceneFormatting,
    chunks: string[],
    subsceneSeparator: string = RecommendationDefaults.SUBSCENE_TOKEN,
): SubsceneFormatting[] {
    const highlights = splitHighlightsAcrossSubscenes(
        formatting.highlights,
        chunks,
        subsceneSeparator,
    );
    const bold = splitHighlightsAcrossSubscenes(formatting.bold, chunks, subsceneSeparator);
    const italic = splitHighlightsAcrossSubscenes(formatting.italic, chunks, subsceneSeparator);
    const underline = splitHighlightsAcrossSubscenes(
        formatting.underline,
        chunks,
        subsceneSeparator,
    );
    return chunks.map((_, index) => ({
        highlights: highlights[index] ?? [],
        bold: bold[index] ?? [],
        italic: italic[index] ?? [],
        underline: underline[index] ?? [],
    }));
}

type TextboxTypes = 'text' | 'group' | 'fancy captions' | 'caption_asset';
export function trackShowLockedMovableTextMessage({
    type,
    sceneId,
}: {
    type: TextboxTypes;
    sceneId: string;
}) {
    analytics.track('Show locked movable text message', {
        type,
        sceneId,
    });
}

export function isAnyCaptionsManagerAsset(assetId: string, sceneGroup: SceneGroup): boolean {
    return assetId === sceneGroup.captions.asset.id;
}

export function getXSceneSnapPoints(sceneWidth: number, sceneHeight: number): SnapPoint[] {
    return [
        { value: 0, min: 0, max: sceneHeight }, // left edge of the scene
        { value: sceneWidth, min: 0, max: sceneHeight }, // right edge of the scene
        { value: sceneWidth / 2, min: 0, max: sceneHeight }, // scene center line
    ];
}

export function getYSceneSnapPoints(sceneWidth: number, sceneHeight: number): SnapPoint[] {
    return [
        { value: 0, min: 0, max: sceneWidth }, // top edge of the scene
        { value: sceneHeight, min: 0, max: sceneWidth }, // bottom edge of the scene
        { value: sceneHeight / 2, min: 0, max: sceneWidth }, // scene center line
    ];
}

export function clampAssetMovementToKeepVisible({
    bounds,
    deltaX,
    deltaY,
    sceneWidth,
    sceneHeight,
}: {
    bounds: { x: number; y: number; width: number; height: number };
    deltaX: number;
    deltaY: number;
    sceneWidth: number;
    sceneHeight: number;
}): { actualDeltaX: number; actualDeltaY: number } {
    const MIN_VISIBLE_PX = 20;

    // Calculate new bounds position and clamp to keep MIN_VISIBLE_PX visible
    const newBoundsX = Math.max(
        MIN_VISIBLE_PX - bounds.width,
        Math.min(sceneWidth - MIN_VISIBLE_PX, bounds.x + deltaX),
    );
    const newBoundsY = Math.max(
        MIN_VISIBLE_PX - bounds.height,
        Math.min(sceneHeight - MIN_VISIBLE_PX, bounds.y + deltaY),
    );

    return {
        actualDeltaX: newBoundsX - bounds.x,
        actualDeltaY: newBoundsY - bounds.y,
    };
}

export function getAssetSnapPoints(
    scene: Readonly<Scene>,
    currentAssetId: string,
): { xSnapPoints: SnapPoint[]; ySnapPoints: SnapPoint[] } {
    const xPoints: SnapPoint[] = [];
    const yPoints: SnapPoint[] = [];

    const xSceneSnapPoints = getXSceneSnapPoints(
        scene.root.referenceResolution.width,
        scene.root.referenceResolution.height,
    );
    const ySceneSnapPoints = getYSceneSnapPoints(
        scene.root.referenceResolution.width,
        scene.root.referenceResolution.height,
    );

    const currentAsset = scene.getAssetById(currentAssetId);
    const currentAssetIds = [currentAssetId];
    if (currentAsset && VideoData.isAssetGroup(currentAsset)) {
        currentAsset.getAssets().forEach((c) => {
            currentAssetIds.push(c.id);
        });
    }
    // Push x and y scene snap points first
    xPoints.push(...xSceneSnapPoints);
    yPoints.push(...ySceneSnapPoints);
    scene
        .getAssets()
        .filter((asset) => {
            // For watermark assets, only include if they're actually visible
            if (asset.name === WATERMARK_ASSET_NAME) {
                const mediaAsset = asset as TimeAwareMediaAsset;
                return (
                    scene.watermark.sourceData !== VideoData.WATERMARK_NONE &&
                    mediaAsset.sourceData &&
                    mediaAsset.sourceData.source_id !== 'invisible'
                );
            }
            // Include AssetGroups --> isEditable is set to false by default and we want to be able to snap to AssetGroups
            return asset.isEditable || VideoData.isAssetGroup(asset);
        })
        .filter((asset) => !currentAssetIds.includes(asset.id))
        .forEach((asset) => {
            const normalizedBounds = getNormalizedAssetBounds(asset);

            const x = normalizedBounds.normalizedX * scene.root.referenceResolution.width;
            const y = normalizedBounds.normalizedY * scene.root.referenceResolution.height;
            const width = normalizedBounds.normalizedWidth * scene.root.referenceResolution.width;
            const height =
                normalizedBounds.normalizedHeight * scene.root.referenceResolution.height;

            // Skip assets with zero dimensions
            if (width === 0 || height === 0) {
                return;
            }

            // Add X snap points
            xPoints.push(
                { value: x, min: y, max: y + height }, // left edge
                { value: x + width / 2, min: y, max: y + height }, // center
                { value: x + width, min: y, max: y + height }, // right edge
            );

            // Add Y snap points
            yPoints.push(
                { value: y, min: x, max: x + width }, // top edge
                { value: y + height / 2, min: x, max: x + width }, // center
                { value: y + height, min: x, max: x + width }, // bottom edge
            );
        });
    return { xSnapPoints: xPoints, ySnapPoints: yPoints };
}

export function getSceneBordersAndMidPointsSnapValues({
    sceneWidth,
    sceneHeight,
}: {
    sceneWidth: number;
    sceneHeight: number;
}): number[] {
    return [0, sceneWidth / 2, sceneWidth, sceneHeight / 2, sceneHeight];
}
